import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Inbox,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock4,
  ChevronDown,
  StickyNote,
  UserPlus,
  RefreshCw,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const STATUSES = [
  { v: "new", l: "New", c: "bg-blue-50 text-blue-700 border-blue-200" },
  { v: "contacted", l: "Contacted", c: "bg-amber-50 text-amber-700 border-amber-200" },
  { v: "qualified", l: "Qualified", c: "bg-violet-50 text-violet-700 border-violet-200" },
  { v: "demo_booked", l: "Demo booked", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { v: "won", l: "Won", c: "bg-green-100 text-green-800 border-green-300" },
  { v: "lost", l: "Lost", c: "bg-rose-50 text-rose-700 border-rose-200" },
];

function statusMeta(v) {
  return STATUSES.find((s) => s.v === v) || STATUSES[0];
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SaasInquiriesPanel() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [r, t] = await Promise.all([
        api.get("/super/saas-inquiries", { params }),
        api.get("/super/saas-inquiries/team"),
      ]);
      setRows(r.data.inquiries || []);
      setCounts(r.data.counts || {});
      setTeam(t.data.members || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateRow = async (id, patch, msg) => {
    try {
      const r = await api.patch(`/super/saas-inquiries/${id}`, patch);
      setRows((cur) => cur.map((x) => (x.id === id ? r.data.inquiry : x)));
      if (msg) toast.success(msg);
      // refresh counts
      const fresh = await api.get("/super/saas-inquiries", {
        params: filter === "all" ? {} : { status: filter },
      });
      setCounts(fresh.data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalAll = counts.all ?? rows.length;

  return (
    <div
      className="mt-10 rounded-xl border border-slate-200 bg-white"
      data-testid="saas-inquiries-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#C9A961]/15 text-[#7A6428]">
            <Inbox className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0A2540]">
              SaaS Inquiries
            </h2>
            <p className="text-xs text-slate-500">
              Prospects from <code className="bg-slate-100 px-1 rounded">/saas</code> landing page
              · {totalAll} total
            </p>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="saas-inquiries-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#0A2540] px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/40">
        <FilterChip
          label="All"
          n={counts.all ?? 0}
          active={filter === "all"}
          onClick={() => setFilter("all")}
          testid="filter-all"
        />
        {STATUSES.map((s) => (
          <FilterChip
            key={s.v}
            label={s.l}
            n={counts[s.v] || 0}
            active={filter === s.v}
            onClick={() => setFilter(s.v)}
            testid={`filter-${s.v}`}
            colorClass={s.c}
          />
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          Loading inquiries…
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <Inbox className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">
            No inquiries{filter !== "all" ? ` with status "${filter}"` : ""} yet.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            They'll appear here automatically when prospects submit the /saas form.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="saas-inquiries-table">
            <TableHeader>
              <TableRow>
                <TableHead>Prospect</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Best time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="whitespace-nowrap">Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <InquiryRow
                  key={r.id}
                  row={r}
                  team={team}
                  onChangeStatus={(v) =>
                    updateRow(r.id, { status: v }, "Status updated")
                  }
                  onAssign={(v) =>
                    updateRow(
                      r.id,
                      { assigned_to: v },
                      v ? "Assigned" : "Unassigned"
                    )
                  }
                  onEditNotes={() => setEditingNotes(r)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NotesDialog
        row={editingNotes}
        onClose={() => setEditingNotes(null)}
        onSave={async (text) => {
          await updateRow(editingNotes.id, { notes: text }, "Notes saved");
          setEditingNotes(null);
        }}
      />
    </div>
  );
}

function FilterChip({ label, n, active, onClick, testid, colorClass }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
        active
          ? "bg-[#0A2540] text-white border-[#0A2540]"
          : colorClass
          ? `${colorClass} hover:opacity-80`
          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
      }`}
    >
      <span className="font-medium">{label}</span>
      <span
        className={`tabular-nums text-[10px] px-1.5 py-px rounded-full ${
          active ? "bg-white/20" : "bg-black/5"
        }`}
      >
        {n}
      </span>
    </button>
  );
}

function InquiryRow({ row, team, onChangeStatus, onAssign, onEditNotes }) {
  const sm = statusMeta(row.status);
  const waLink = `https://wa.me/${(row.whatsapp || row.phone || "").replace(
    /[^0-9]/g,
    ""
  )}`;
  const telLink = `tel:${(row.phone || "").replace(/\D/g, "")}`;
  return (
    <TableRow data-testid={`inquiry-row-${row.id}`}>
      <TableCell>
        <div className="font-medium text-[#0A2540]">{row.name}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
          <Mail className="h-3 w-3" />
          {row.email}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm text-[#0A2540]">{row.company_name}</div>
        {row.location && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" />
            {row.location}
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <a
            href={telLink}
            data-testid={`inquiry-call-${row.id}`}
            className="inline-flex items-center gap-1 text-[11px] text-[#0A2540] hover:text-[#1E3A5F] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50"
            title={row.phone}
          >
            <Phone className="h-3 w-3" /> Call
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid={`inquiry-wa-${row.id}`}
            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-200 rounded px-2 py-1 hover:bg-emerald-50"
            title={row.whatsapp || row.phone}
          >
            <MessageCircle className="h-3 w-3" /> WA
          </a>
        </div>
      </TableCell>
      <TableCell>
        {row.best_time ? (
          <span className="text-xs text-slate-600 inline-flex items-center gap-1">
            <Clock4 className="h-3 w-3 text-[#C9A961]" />
            {row.best_time}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </TableCell>
      <TableCell>
        <Select
          value={row.status}
          onValueChange={onChangeStatus}
        >
          <SelectTrigger
            className={`h-8 w-[140px] text-xs ${sm.c}`}
            data-testid={`inquiry-status-${row.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s.v} value={s.v} className="text-xs">
                {s.l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Select
          value={row.assigned_to || "__unassigned__"}
          onValueChange={(v) => onAssign(v === "__unassigned__" ? "" : v)}
        >
          <SelectTrigger
            className="h-8 w-[170px] text-xs"
            data-testid={`inquiry-assign-${row.id}`}
          >
            <SelectValue placeholder="Unassigned">
              {row.assigned_to_name ? (
                <span className="inline-flex items-center gap-1.5">
                  <UserPlus className="h-3 w-3" /> {row.assigned_to_name}
                </span>
              ) : (
                <span className="text-slate-400 inline-flex items-center gap-1.5">
                  <UserPlus className="h-3 w-3" /> Unassigned
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__unassigned__" className="text-xs">
              Unassigned
            </SelectItem>
            {team.map((m) => (
              <SelectItem key={m.id} value={m.id} className="text-xs">
                {m.name || m.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <button
          onClick={onEditNotes}
          data-testid={`inquiry-notes-${row.id}`}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#0A2540] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 max-w-[200px]"
          title={row.internal_notes || "Add notes"}
        >
          <StickyNote className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {row.internal_notes ? row.internal_notes.slice(0, 30) : "Add notes"}
          </span>
        </button>
      </TableCell>
      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
        {fmtDate(row.created_at)}
      </TableCell>
    </TableRow>
  );
}

function NotesDialog({ row, onClose, onSave }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setText(row?.internal_notes || "");
  }, [row]);
  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inquiry-notes-dialog">
        <DialogHeader>
          <DialogTitle>
            Notes — {row.name} · {row.company_name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-2 flex flex-wrap gap-x-3 gap-y-1">
          <span>📞 {row.phone}</span>
          {row.whatsapp && row.whatsapp !== row.phone && <span>WA {row.whatsapp}</span>}
          <span>✉ {row.email}</span>
          {row.location && <span>📍 {row.location}</span>}
          {row.best_time && <span>🕒 {row.best_time}</span>}
        </div>
        {row.message && (
          <div className="text-sm text-slate-700 italic bg-slate-50 rounded-md p-3 border border-slate-100">
            "{row.message}"
          </div>
        )}
        <Textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Internal notes (visible only to super-admin team)…"
          data-testid="inquiry-notes-textarea"
          className="mt-2"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(text);
              } finally {
                setSaving(false);
              }
            }}
            className="bg-[#0A2540] hover:bg-[#1E3A5F] text-white"
            data-testid="inquiry-notes-save"
          >
            {saving ? "Saving…" : "Save notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
